import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { Box, Button, CircularProgress, IconButton, Typography } from '@mui/material';
import { Close, RestartAlt } from '@mui/icons-material';

import { Colors, isLightTheme } from '../config/default';
import { buildAssistantUrl } from '../utils/assistantUrl';
import { describeAssistantContext } from '../utils/assistantPageContext';
import { useAssistant } from '../contexts/assistantContext';
import { ASK_ASSISTANT_TITLE, supportAssistantOrigin } from '../constants/appConstants';

// Matches the AppBar height in components/topNav.jsx. The panel starts below the
// nav rather than at the top of the viewport so the nav — including the button
// that closes this panel again — stays visible and clickable while it is open.
const NAV_HEIGHT = '4.2rem';

// How long to wait after the app mounts before creating the (hidden) iframe.
//
// The Space this iframe loads sleeps when idle and can take tens of seconds to
// wake, so it is warmed in the background instead of on the user's click. That
// warming must never be the reason the app feels slow, so it is deferred twice:
// this timeout lets the first paint and the login API burst finish (and lets
// App's own route-chunk prefetch take the first idle slot, because navigation
// speed matters more than the assistant's first open), and requestIdleCallback
// then waits for the browser to actually be idle.
const WARM_DELAY_MS = 2000;

const SLIDE_MS = 220;

// Message the Space listens for. It answers "what does 'this page' mean?" by
// telling the assistant which screen is open.
//
// Sent by postMessage rather than added to the iframe's URL on purpose: the URL
// is the one thing that must never change (see buildAssistantUrl), and the user
// changes pages constantly. A query parameter would reload the chat on every
// navigation — the exact opposite of what the panel is for.
const CONTEXT_MESSAGE_TYPE = 'bh:page-context';

// The Space announces itself once its own listener is installed.
//
// Posting on the iframe's `load` event is not enough: `load` fires when the
// document and its subresources are done, but Gradio only registers our
// listener after it has executed its bundle, fetched /config and mounted — which
// normally finishes later. Without this handshake the first context message is
// delivered to a window that is not listening yet and is silently dropped, and
// the assistant stays context-blind until the user happens to change route.
const READY_MESSAGE_TYPE = 'bh:assistant-ready';

// Sent by the Space when Escape is pressed inside the chat. Keyboard events do
// not cross an iframe boundary, so without this the panel's own Escape handler
// is dead exactly when the panel is in use.
const ESCAPE_MESSAGE_TYPE = 'bh:assistant-escape';

/**
 * The support assistant, docked to the right of every authenticated page.
 *
 * Mounted once by App, ABOVE the router, and never unmounted while the user is
 * signed in. That placement is the whole design:
 *
 *   - The iframe is created once and kept. Navigating between pages, switching
 *     theme, or closing and reopening the panel does not reload it, so the
 *     conversation survives and the Space is woken at most once per page load.
 *   - Opening the panel is a CSS transform, not a mount. By the time the user
 *     clicks, the Space is already awake.
 *
 * Two rules follow from that and must not be broken by later edits:
 * the iframe must never be conditionally rendered or moved to a different
 * parent (React would tear it down and reload it), and its `src` must stay
 * byte-identical across renders (a changed src is also a reload).
 */
export default function AskAssistant() {
  const { open, closeAssistant } = useAssistant();
  const { pathname } = useLocation();

  const user = useSelector((state) => state.signIn?.signIn?.cognitoRes?.idToken?.payload);
  const identityReady = Boolean(user?.email);
  const tabs = useSelector((state) => state.tabs?.tabs);

  const pageContext = describeAssistantContext(pathname, tabs);

  // Has the iframe been created yet? Once true it never goes back to false.
  const [warmed, setWarmed] = useState(false);
  const [loaded, setLoaded] = useState(false);
  // Bumping this remounts the iframe — the one place a reload is intended.
  const [reloadKey, setReloadKey] = useState(0);

  const iframeRef = useRef(null);
  const srcRef = useRef(null);
  const mountedRef = useRef(false);
  // Lets the ready handshake below read the current context without
  // re-subscribing every time the user changes page.
  const pageContextRef = useRef('');
  pageContextRef.current = pageContext;

  // The full-page /FAQ route already renders this same chatbot. Hide the panel
  // there rather than unmounting it, so two copies are never on screen at once
  // and the warmed iframe still survives the visit.
  // Compared case-insensitively because React Router 6 matches routes that way
  // by default: /faq renders the full-page FAQ just as /FAQ does, and an exact
  // string compare would leave the panel open beside it — two live copies of the
  // same Space for one user.
  const suppressed = pathname.toLowerCase() === '/faq';
  const visible = open && !suppressed;

  // Warm in the background once we know who the user is. Waiting for the
  // identity matters: it is baked into the src, and a src built before the
  // profile rehydrated would have to change later — which would reload the
  // iframe and throw away both the warm-up and any conversation in it.
  useEffect(() => {
    if (warmed || !identityReady) return undefined;

    const requestIdle = window.requestIdleCallback || ((cb) => setTimeout(() => cb(), 300));
    const cancelIdle = window.cancelIdleCallback || clearTimeout;

    let idleHandle;
    const timer = setTimeout(() => {
      idleHandle = requestIdle(() => setWarmed(true));
    }, WARM_DELAY_MS);

    return () => {
      clearTimeout(timer);
      if (idleHandle !== undefined) cancelIdle(idleHandle);
    };
  }, [warmed, identityReady]);

  // A user who opens the panel inside the warm-up delay should not have to wait
  // for it to elapse.
  useEffect(() => {
    if (visible) setWarmed(true);
  }, [visible]);

  useEffect(() => {
    if (!visible) return undefined;

    const onKeyDown = (event) => {
      if (event.key === 'Escape') closeAssistant();
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [visible, closeAssistant]);

  // Put the caret in the chat on open so the user can type straight away —
  // but not on the very first run, because the panel can come back open from the
  // last session and focusing it on page load would pull focus away from the
  // page the user actually landed on.
  //
  // The condition is "has this component mounted", NOT "has the panel been
  // visible before". Keying it on visibility skipped the user's first real open
  // too, since the mount run returns early while the panel is still closed.
  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      return;
    }
    if (visible) iframeRef.current?.focus();
  }, [visible]);

  const postContext = useCallback((context) => {
    const frame = iframeRef.current?.contentWindow;
    if (!frame) return;

    // Targeted at the Space's exact origin, never '*': the value is read into
    // the assistant's system prompt.
    frame.postMessage({ type: CONTEXT_MESSAGE_TYPE, context }, supportAssistantOrigin());
  }, []);

  // Listen for the Space announcing that its own listener is up, and answer with
  // the current screen. This is what makes context arrive on the FIRST open
  // rather than only after the user changes route.
  //
  // Subscribed on mount, before the iframe exists, so the announcement cannot
  // arrive before we are listening for it.
  useEffect(() => {
    const onMessage = (event) => {
      if (event.origin !== supportAssistantOrigin()) return;
      if (event.source !== iframeRef.current?.contentWindow) return;

      if (event.data?.type === READY_MESSAGE_TYPE) {
        postContext(pageContextRef.current);
      } else if (event.data?.type === ESCAPE_MESSAGE_TYPE) {
        closeAssistant();
      }
    };

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [postContext, closeAssistant]);

  // Re-report whenever the screen changes. Also fires on load — harmless if the
  // handshake got there first, and the safety net if the announcement is ever
  // missed. `loaded` goes false and back to true on Clear, so a reset
  // conversation is told where it is too.
  useEffect(() => {
    if (!loaded) return;
    postContext(pageContext);
  }, [loaded, pageContext, postContext]);

  const shouldRender = warmed && identityReady;

  // Resolved once, on the render that first creates the iframe, and then frozen
  // in a ref — see buildAssistantUrl for why the theme is not kept in sync.
  if (shouldRender && !srcRef.current) {
    srcRef.current = buildAssistantUrl({
      email: user?.email,
      name: user?.name,
      theme: isLightTheme() ? 'light' : 'dark'
    });
  }

  if (!shouldRender) return null;

  const resetConversation = () => {
    setLoaded(false);
    setReloadKey((key) => key + 1);
  };

  return (
    <Box
      data-testid="assistant-panel"
      aria-hidden={!visible}
      sx={{
        position: 'fixed',
        top: NAV_HEIGHT,
        right: 0,
        bottom: 0,
        width: { xs: '100%', sm: '26rem' },
        display: 'flex',
        flexDirection: 'column',
        bgcolor: Colors.BG_BLACK,
        borderLeft: `1px solid ${Colors.DEEPGREY}`,
        boxShadow: '-8px 0 24px rgba(0, 0, 0, 0.35)',
        // Deliberately below MUI's modal layer: a dialog the user opened is
        // blocking and belongs on top of this. Above everything else.
        zIndex: 1250,
        transform: visible ? 'translateX(0)' : 'translateX(100%)',
        // `visibility` (not just the transform) is what keeps a closed panel out
        // of the tab order and unable to take focus — an off-screen but visible
        // iframe can still steal the caret mid-typing when Gradio autofocuses
        // its input. It is delayed on close so the panel stays painted while it
        // slides out, and applied immediately on open.
        visibility: visible ? 'visible' : 'hidden',
        transition: visible
          ? `transform ${SLIDE_MS}ms ease-out, visibility 0s`
          : `transform ${SLIDE_MS}ms ease-in, visibility 0s linear ${SLIDE_MS}ms`,
        pointerEvents: visible ? 'auto' : 'none'
      }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '0.5rem',
          px: '0.75rem',
          py: '0.5rem',
          bgcolor: Colors.NAV_BLACK,
          borderBottom: `1px solid ${Colors.DEEPGREY}`,
          flexShrink: 0
        }}>
        <Typography sx={{ color: Colors.WHITE, fontSize: '0.9rem', fontWeight: 600 }}>
          {ASK_ASSISTANT_TITLE}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
          <Button
            onClick={resetConversation}
            startIcon={<RestartAlt sx={{ fontSize: '16px' }} />}
            sx={{
              color: Colors.WHITE,
              fontSize: '0.7rem',
              border: `1px solid ${Colors.DARKBLUE}`,
              borderRadius: '10px',
              px: '0.6rem'
            }}>
            Clear
          </Button>
          <IconButton onClick={closeAssistant} aria-label="Close assistant">
            <Close sx={{ color: Colors.WHITE, fontSize: '18px' }} />
          </IconButton>
        </Box>
      </Box>

      <Box sx={{ position: 'relative', flex: 1, minHeight: 0 }}>
        {visible && !loaded && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: Colors.BG_BLACK,
              zIndex: 1
            }}>
            <CircularProgress />
          </Box>
        )}

        <iframe
          key={reloadKey}
          ref={iframeRef}
          src={srcRef.current}
          onLoad={() => setLoaded(true)}
          title={ASK_ASSISTANT_TITLE}
          style={{
            border: 'none',
            height: '100%',
            width: '100%',
            display: 'block',
            backgroundColor: Colors.BG_BLACK
          }}
          allow="clipboard-write"
        />
      </Box>
    </Box>
  );
}
